import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/i18n/useTranslation';
import type { MapLegendResponse } from '@/lib/api/map';
import { useMapDesignationFilter } from '@/context/MapDesignationFilterContext';
import { loadMapLegend, peekMapLegend, runWhenIdle } from '@/lib/map/map-cache';
import MapLegendEntries from './MapLegendEntries';
import MapLegendPanel from './MapLegendPanel';
import styles from './MapLegend.module.css';

const OVERLAY_PREVIEW_MAX = 4;

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; data: MapLegendResponse }
  | { status: 'error' };

type Props = {
  className?: string;
  /** Floating collapsible widget on the map (Map screen). */
  variant?: 'card' | 'overlay';
};

export default function MapLegend({ className, variant = 'card' }: Props) {
  const t = useTranslation();
  const { registerEntries, enabledCount, isMapFiltered } = useMapDesignationFilter();
  const [state, setState] = useState<LoadState>(() =>
    peekMapLegend() ? { status: 'ready', data: peekMapLegend()! } : { status: 'loading' },
  );
  const [panelOpen, setPanelOpen] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);

  const openPanel = () => {
    if (detailsRef.current) {
      detailsRef.current.open = false;
    }
    setPanelOpen(true);
  };

  useEffect(() => {
    const cached = peekMapLegend();
    if (cached) {
      registerEntries(cached.entries);
      setState({ status: 'ready', data: cached });
      return;
    }

    let cancelled = false;
    const cancelIdle = runWhenIdle(() => {
      void loadMapLegend()
        .then((data) => {
          if (!cancelled) {
            registerEntries(data.entries);
            setState({ status: 'ready', data });
          }
        })
        .catch(() => {
          if (!cancelled) {
            setState({ status: 'error' });
          }
        });
    });

    return () => {
      cancelled = true;
      cancelIdle();
    };
  }, [registerEntries]);

  const summaryEntries =
    state.status === 'ready'
      ? state.data.summaryEntries.length > 0
        ? state.data.summaryEntries
        : state.data.entries.slice(0, OVERLAY_PREVIEW_MAX)
      : [];

  const previewEntries =
    variant === 'overlay' ? summaryEntries.slice(0, OVERLAY_PREVIEW_MAX) : summaryEntries;

  const fullCount = state.status === 'ready' ? state.data.totalClasses : 0;
  const showFilterButton = state.status === 'ready' && state.data.entries.length > 0;
  const filterButtonLabel =
    state.status === 'ready'
      ? isMapFiltered
        ? t.map.legend.mapFilterActive
            .replace('{shown}', String(enabledCount))
            .replace('{total}', String(fullCount))
        : t.map.legend.mapFilterOpen.replace('{count}', String(fullCount))
      : '';

  const panel =
    state.status === 'ready' ? (
      <MapLegendPanel
        open={panelOpen}
        layerName={state.data.layerName}
        entries={state.data.entries}
        onClose={() => setPanelOpen(false)}
      />
    ) : null;

  if (variant === 'overlay') {
    return (
      <>
        {!panelOpen ? (
          <details
            ref={detailsRef}
            className={[styles.overlayWidget, className].filter(Boolean).join(' ')}
            data-testid="map-legend"
          >
            <summary className={styles.overlaySummary} data-testid="map-legend-toggle">
              <span className={styles.overlayTitle}>{t.map.legend.title}</span>
              <span className={styles.overlayChevron} aria-hidden />
            </summary>
            <div className={styles.overlayBody}>
              {state.status === 'loading' ? (
                <p className={styles.status} data-testid="map-legend-loading">
                  {t.map.legend.loading}
                </p>
              ) : null}
              {state.status === 'error' ? (
                <p className={styles.status} role="alert" data-testid="map-legend-error">
                  {t.map.legend.loadError}
                </p>
              ) : null}
              {state.status === 'ready' ? (
                <>
                  <p className={styles.overlayHint}>{t.map.legend.summaryHint}</p>
                  <MapLegendEntries entries={previewEntries} compact />
                  {showFilterButton ? (
                    <button
                      type="button"
                      className={styles.fullBtn}
                      onClick={openPanel}
                      data-testid="map-legend-show-full"
                    >
                      {filterButtonLabel}
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
          </details>
        ) : null}
        {panel}
      </>
    );
  }

  const rootClass = [styles.legend, className].filter(Boolean).join(' ');

  return (
    <>
      <aside className={rootClass} data-testid="map-legend" aria-label={t.map.legend.title}>
        <header className={styles.header}>
          <h2 className={styles.title}>{t.map.legend.title}</h2>
          {showFilterButton ? (
            <button
              type="button"
              className={styles.fullBtn}
              onClick={openPanel}
              data-testid="map-legend-show-full"
            >
              {filterButtonLabel}
            </button>
          ) : null}
        </header>
        {state.status === 'loading' ? (
          <p className={styles.status} data-testid="map-legend-loading">
            {t.map.legend.loading}
          </p>
        ) : null}
        {state.status === 'error' ? (
          <p className={styles.status} role="alert" data-testid="map-legend-error">
            {t.map.legend.loadError}
          </p>
        ) : null}
        {state.status === 'ready' ? (
          <MapLegendEntries entries={previewEntries} className={styles.entries} />
        ) : null}
        <p className={styles.hint}>{t.map.legend.approvalDateHint}</p>
      </aside>
      {panel}
    </>
  );
}
